import { todayIsoDate } from "../misc/formatDate";

/**
 * Dashboard numbers are computed by the backend: every tile and every group is
 * a filtered `getList` on `sponsorings` using the PostgREST style operators the
 * data providers understand (`@in`, `@gte`, `@lt`, `@is`).
 *
 * All date math happens on `YYYY-MM-DD` strings so it is timezone safe: the
 * strings are compared lexicographically (which equals chronological order) and
 * day arithmetic goes through UTC midnight, never through local Date parsing.
 */

const MS_PER_DAY = 86_400_000;

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/** Today as `YYYY-MM-DD` in the user's own timezone. */
export const todayIso = todayIsoDate;

/** True for a well-formed `YYYY-MM-DD` string. */
export const isIsoDate = (value: unknown): value is string =>
  typeof value === "string" && ISO_DATE_REGEX.test(value);

const isoToUtcMs = (iso: string) => {
  const [year, month, day] = iso.split("-").map(Number);
  return Date.UTC(year, month - 1, day);
};

/** Shift a `YYYY-MM-DD` string by whole days, staying in `YYYY-MM-DD`. */
export const addDaysIso = (iso: string, days: number): string =>
  new Date(isoToUtcMs(iso) + days * MS_PER_DAY).toISOString().slice(0, 10);

/**
 * Whole days between two `YYYY-MM-DD` strings; negative when `to` lies before
 * `from`.
 */
export const daysBetweenIso = (from: string, to: string): number =>
  Math.round((isoToUtcMs(to) - isoToUtcMs(from)) / MS_PER_DAY);

/** PostgREST `@in` value: `["a", "b"]` becomes `"(a,b)"`. */
export const inFilterValue = (values: string[]) => `(${values.join(",")})`;

/** Sponsorings ending within this many days are "soon". */
export const SOON_DAYS = 30;

/** Outer horizon of the "Aflopende sponsoringen" list. */
export const HORIZON_DAYS = 90;

export type SponsoringFilter = Record<string, string | null>;

export type DashboardFilters = {
  /** Running sponsorships */
  active: SponsoringFilter;
  /** Running, end date already passed */
  expired: SponsoringFilter;
  /** Running, ending today through today + SOON_DAYS */
  within30: SponsoringFilter;
  /** Running, ending today through today + HORIZON_DAYS */
  within90: SponsoringFilter;
  /** Running without an end date */
  withoutEndDate: SponsoringFilter;
  /** Everything in a board (funnel) stage */
  openLeads: SponsoringFilter;
  /** Running and ending before the end of the horizon (expired included) */
  endingSoonList: SponsoringFilter;
};

export const buildDashboardFilters = ({
  activeStatuses,
  boardStages,
  today = todayIso(),
}: {
  activeStatuses: string[];
  boardStages: string[];
  today?: string;
}): DashboardFilters => {
  const active: SponsoringFilter = {
    "stage@in": inFilterValue(activeStatuses),
  };
  return {
    active,
    expired: { ...active, "end_date@lt": today },
    within30: {
      ...active,
      "end_date@gte": today,
      "end_date@lt": addDaysIso(today, SOON_DAYS + 1),
    },
    within90: {
      ...active,
      "end_date@gte": today,
      "end_date@lt": addDaysIso(today, HORIZON_DAYS + 1),
    },
    withoutEndDate: { ...active, "end_date@is": null },
    openLeads: { "stage@in": inFilterValue(boardStages) },
    endingSoonList: {
      ...active,
      "end_date@lt": addDaysIso(today, HORIZON_DAYS + 1),
    },
  };
};

export type ExpiringGroupKey = "expired" | "within30" | "within90";

/** Which group of the "Aflopende sponsoringen" list an end date belongs to. */
export const expiringGroupOf = (
  endDate: string,
  today: string,
): ExpiringGroupKey => {
  const days = daysBetweenIso(today, endDate);
  if (days < 0) return "expired";
  if (days <= SOON_DAYS) return "within30";
  return "within90";
};
